<script lang="ts" setup generic="ListItemType extends GenericListItemType">
import { GenericListHeading, GenericListItemType } from '@/core/models/generic-list.interface';

//#region Props & Emits
defineProps<{
   name: string;
   hasActions: boolean;
   loading: boolean;
   visibleHeadings: Array<GenericListHeading<ListItemType>>;
   advancedHeadings: boolean;
   activeSort: string;
   isDragging: boolean;
   dragEnterIndex: number;
   shortenHeadingLength: boolean;
}>();

const emits = defineEmits<{
   sortBy: [{ key: string; order: 'asc' | 'desc' }];
   dragStart: [{ event: DragEvent; index: number }];
   dragEnd: [];
   dragEnter: [{ event: DragEvent; index: number }];
   toggleShowMenuIcon: [{ index: number; value: boolean }];
   pinHeading: [{ index: number; value: boolean }];
   toggleHeading: [{ index: number; value: boolean; refresh?: boolean }];
}>();

const allItemsSelected = defineModel<boolean>('allItemsSelected', { default: false });
//#endregion
</script>

<template>
   <th v-if="hasActions">
      <div :class="'generic-list-headings__heading -select-all'" :style="{ width: '3rem' }">
         <NxtCheckbox v-model="allItemsSelected" :disabled="loading" :name="`${name}-select-all`" />
      </div>
   </th>
   <slot name="headings">
      <th
         v-for="(heading, index) in visibleHeadings"
         :key="index"
         :draggable="advancedHeadings && !!heading.label"
         :style="heading.style"
         :class="[{ '-drag-entered': isDragging && dragEnterIndex === index, '-pinned': heading.pinned }]"
         @dragstart="emits('dragStart', { event: $event, index })"
         @dragover.prevent
         @dragend="emits('dragEnd')"
         @dragenter="emits('dragEnter', { event: $event, index })"
         @mouseenter="emits('toggleShowMenuIcon', { index: heading.realIndex ?? -1, value: true })"
         @mouseleave="emits('toggleShowMenuIcon', { index: heading.realIndex ?? -1, value: false })"
      >
         <div :class="'generic-list-headings__heading '" :style="{ justifyContent: heading.alignment }">
            <NxtHoverCard v-if="shortenHeadingLength">
               <template #hover-item>
                  <span class="generic-list-headings__heading__short">{{ heading.label }}</span>
               </template>

               {{ heading.label }}
            </NxtHoverCard>
            <span v-else>
               {{ heading.label }}
            </span>

            <!-- Sorting -->
            <div v-if="heading.sortKey" class="d-flex flex-column">
               <button
                  :class="['btn btn-icon', { '-active': activeSort.includes(heading.sortKey) && !activeSort.startsWith('-') }]"
                  @click="emits('sortBy', { key: heading.sortKey!, order: 'asc' })"
               >
                  <i class="fas fa-chevron-up" style="font-size: 0.9rem"></i>
               </button>
               <button
                  :class="['btn btn-icon', { '-active': activeSort.includes(heading.sortKey) && activeSort.startsWith('-') }]"
                  @click="emits('sortBy', { key: heading.sortKey!, order: 'desc' })"
               >
                  <i class="fas fa-chevron-down" style="font-size: 0.9rem"></i>
               </button>
            </div>

            <!-- Columns -->
            <div
               :style="{ minWidth: '24px' }"
               @mouseenter="emits('toggleShowMenuIcon', { index: heading.realIndex ?? -1, value: true })"
               @mouseleave="emits('toggleShowMenuIcon', { index: heading.realIndex ?? -1, value: false })"
            >
               <NxtHoverCard
                  v-if="heading.label && advancedHeadings && heading.showMenuIcon"
                  :class="'generic-list-headings__heading__menu'"
                  :mode="'light'"
                  :clickable="true"
                  :is-card-hoverable="true"
                  :with-triangle="false"
               >
                  <template #hover-item>
                     <NxtIconButton :name="'fa-ellipsis-vertical'" />
                  </template>

                  <div
                     class="d-flex flex-column"
                     @mouseenter="emits('toggleShowMenuIcon', { index: heading.realIndex ?? -1, value: true })"
                     @mouseleave="emits('toggleShowMenuIcon', { index: heading.realIndex ?? -1, value: false })"
                  >
                     <button class="p-2" @click="emits('pinHeading', { index: heading.realIndex ?? -1, value: !heading.pinned })">
                        {{ heading.pinned ? $t('actions.unpin') : $t('actions.pin') }}
                     </button>
                     <button class="p-2" @click="emits('toggleHeading', { index: heading.realIndex ?? -1, value: false })">
                        {{ $t('actions.hide') }}
                     </button>
                  </div>
               </NxtHoverCard>
            </div>
         </div>
      </th>
   </slot>
</template>

<style lang="scss" scoped>
.generic-list-headings__heading {
   display: flex;
   align-items: center;
   gap: var(--nxt-gutter);

   .btn.btn-icon {
      color: var(--stroke-default);
      &.-active {
         color: var(--nxt-brand-primary-light);
      }
   }

   &.-select-all {
      justify-content: center;

      > * {
         padding: var(--nxt-gutter-small);
      }
   }

   &__short {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      line-clamp: 3;
      -webkit-box-orient: vertical;
   }
}

.generic-list-headings__heading__menu {
   margin-left: var(--nxt-gutter--negative);
}
</style>
